import React, { useContext, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAddressCard,
  faChartLine,
  faChevronLeft,
  faChevronRight,
  faChevronDown,
  faKey,
  faUser,
  faUsers,
  faArrowRightArrowLeft,
  faSquareParking,
  faFilePdf,
  faCalendar,
  faBookmark,
  faCheck,
  faFileInvoice,
} from "@fortawesome/free-solid-svg-icons";
import { faGear } from "@fortawesome/free-solid-svg-icons/faGear";
import classNames from "classnames";
import { DashboardContext } from "../../../routes/DashboardRoutes";
import SidebarNavItem from "./SidebarNavItem";

const Sidebar = ({ className = "", onNavigate = () => {} }) => {
  const { settings, logo } = useContext(DashboardContext);

  const businessSettings = settings?.business;
  const logoUrl = logo && logo.url;

  const [toggled, setToggled] = useState(false);

  const location = useLocation();

  const { pathname } = location;

  const parkingSlotsEnabled = Boolean(businessSettings?.parking_slots_enabled);
  const onlineReservationEnabled = Boolean(
    settings?.online_reservation?.enabled
  );

  const defaultPath = parkingSlotsEnabled
    ? "/dashboard/bookings/planner"
    : "/dashboard/bookings/active-bookings";

  const matchesPath = (paths) =>
    paths.some((path) => pathname === path || pathname.startsWith(`${path}/`));
  const isDashboardIndex =
    pathname === "/dashboard" || pathname === "/dashboard/";
  const isBookingsActive =
    isDashboardIndex ||
    matchesPath(["/dashboard/bookings", "/dashboard/stays"]);
  const isSettingsActive = matchesPath([
    "/dashboard/settings",
    "/dashboard/bookingsettings",
  ]);

  return (
    <ul
      className={classNames(
        "navbar-nav bg-gradient-primary sidebar sidebar-dark accordion",
        className,
        { toggled }
      )}
      id="accordionSidebar"
    >
      <Link
        className="sidebar-brand d-flex align-items-center justify-content-center"
        to="/dashboard"
        onClick={onNavigate}
      >
        <div className="sidebar-brand-icon">
          {logoUrl && <img src={logoUrl} alt="Logo" />}
        </div>
      </Link>

      <hr className="sidebar-divider my-0" />

      <div className="sidebar-heading mt-3">Buchungen</div>

      <li className={classNames("nav-item", { active: isBookingsActive })}>
        <a
          className={classNames("nav-link", {
            active: isBookingsActive,
            collapsed: !isBookingsActive,
          })}
          href="#"
          data-toggle="collapse"
          data-target="#bookings"
          aria-expanded={isBookingsActive}
          aria-controls="bookings"
          onClick={() => toggled && setToggled(false)}
        >
          <FontAwesomeIcon className="fa-fw mr-2" icon={faUsers} />
          <span>Buchungen</span>
          <FontAwesomeIcon className="accordion-arrow" icon={faChevronDown} />
        </a>
        <div
          id="bookings"
          className={classNames("collapse", {
            show: isBookingsActive,
          })}
          aria-labelledby="headingPages"
          data-parent="#accordionSidebar"
        >
          <div className="bg-white py-2 collapse-inner rounded">
            <h6 className="collapse-header">Buchungen</h6>
            {parkingSlotsEnabled && (
              <Link
                className={classNames("collapse-item", {
                  active:
                    pathname === "/dashboard/bookings/planner" ||
                    (isDashboardIndex &&
                      "/dashboard/bookings/planner" === defaultPath),
                })}
                to="/dashboard/bookings/planner"
                onClick={onNavigate}
              >
                <FontAwesomeIcon className="fa-fw mr-2" icon={faCalendar} />
                <span>Übersicht</span>
              </Link>
            )}
            <Link
              className={classNames("collapse-item", {
                active:
                  pathname === "/dashboard/bookings/active-bookings" ||
                  (isDashboardIndex &&
                    "/dashboard/bookings/active-bookings" === defaultPath),
              })}
              to="/dashboard/bookings/active-bookings"
              onClick={onNavigate}
            >
              <FontAwesomeIcon className="fa-fw mr-2" icon={faUser} />
              <span>Aktiv</span>
            </Link>
            <Link
              className={classNames("collapse-item", {
                active: pathname === "/dashboard/bookings",
              })}
              to="/dashboard/bookings"
              onClick={onNavigate}
            >
              <FontAwesomeIcon className="fa-fw mr-2" icon={faUsers} />
              <span>Alle</span>
            </Link>
          </div>
        </div>
      </li>

      <SidebarNavItem
        to="/dashboard/daily-checkin"
        icon={faCheck}
        label="Tages-Check"
        onNavigate={onNavigate}
      />

      <SidebarNavItem
        to="/dashboard/reservations"
        icon={faBookmark}
        label="Reservierungen"
        visible={onlineReservationEnabled}
        onNavigate={onNavigate}
      />

      <SidebarNavItem
        to="/dashboard/gatelogs"
        icon={faArrowRightArrowLeft}
        label="Aktivitäten"
        onNavigate={onNavigate}
      />

      <hr className="sidebar-divider" />

      <div className="sidebar-heading">Buchhaltung</div>

      <SidebarNavItem
        to="/dashboard/accounting/invoices"
        icon={faFileInvoice}
        label="Rechnungen"
        onNavigate={onNavigate}
      />

      <hr className="sidebar-divider" />

      <div className="sidebar-heading">Statistiken</div>

      <SidebarNavItem
        to="/dashboard/stats"
        icon={faChartLine}
        label="Statistiken"
        onNavigate={onNavigate}
      />

      <hr className="sidebar-divider" />

      <div className="sidebar-heading">Meldungen</div>

      <SidebarNavItem
        to="/dashboard/police-reports"
        icon={faAddressCard}
        label="Polizeimeldung"
        onNavigate={onNavigate}
      />

      <SidebarNavItem
        to="/dashboard/local-tax-daily-reports"
        icon={faFileInvoice}
        label="LTS Meldungen"
        onNavigate={onNavigate}
      />

      <hr className="sidebar-divider" />

      <div className="sidebar-heading">Berichte</div>

      <SidebarNavItem
        to="/dashboard/deposit-cards"
        icon={faFilePdf}
        label="PDF-Dokumente"
        onNavigate={onNavigate}
      />

      <hr className="sidebar-divider" />

      <div className="sidebar-heading">Zutrittsverwaltung</div>

      <SidebarNavItem
        to="/dashboard/access-keys"
        icon={faKey}
        label="Zutrittsverwaltung"
        onNavigate={onNavigate}
      />

      <hr className="sidebar-divider" />

      <div className="sidebar-heading">Sonstiges</div>

      <li className={classNames("nav-item", { active: isSettingsActive })}>
        <a
          className={classNames("nav-link", {
            active: isSettingsActive,
            collapsed: !isSettingsActive,
          })}
          href="#"
          data-toggle="collapse"
          data-target="#settings"
          aria-expanded={isSettingsActive}
          aria-controls="settings"
          onClick={() => toggled && setToggled(false)}
        >
          <FontAwesomeIcon className="fa-fw mr-2" icon={faGear} />
          <span>Einstellungen</span>
          <FontAwesomeIcon className="accordion-arrow" icon={faChevronDown} />
        </a>
        <div
          id="settings"
          className={classNames("collapse", { show: isSettingsActive })}
          aria-labelledby="headingPages"
          data-parent="#accordionSidebar"
        >
          <div className="bg-white py-2 collapse-inner rounded">
            <h6 className="collapse-header">Einstellungen</h6>
            <Link
              className={classNames("collapse-item", {
                active: matchesPath(["/dashboard/settings/business"]),
              })}
              to="/dashboard/settings/business/general"
              onClick={onNavigate}
            >
              <span>Camping</span>
            </Link>
            {parkingSlotsEnabled && (
              <Link
                className={classNames("collapse-item", {
                  active: matchesPath(["/dashboard/settings/booking-planner"]),
                })}
                to="/dashboard/settings/booking-planner"
                onClick={onNavigate}
              >
                <span>Buchungskalender</span>
              </Link>
            )}

            <Link
              className={classNames("collapse-item", {
                active: matchesPath(["/dashboard/settings/pricelist"]),
              })}
              to="/dashboard/settings/pricelist/prices"
              onClick={onNavigate}
            >
              <span>Preisliste</span>
            </Link>
            <Link
              className={classNames("collapse-item", {
                active: matchesPath(["/dashboard/settings/email"]),
              })}
              to="/dashboard/settings/email"
              onClick={onNavigate}
            >
              <span>E-Mail</span>
            </Link>
            {onlineReservationEnabled && (
              <Link
                className={classNames("collapse-item", {
                  active: matchesPath(["/dashboard/settings/reservation"]),
                })}
                to="/dashboard/settings/reservation"
                onClick={onNavigate}
              >
                <span>Reservierungen</span>
              </Link>
            )}
            <Link
              className={classNames("collapse-item", {
                active: matchesPath(["/dashboard/bookingsettings"]),
              })}
              to="/dashboard/bookingsettings"
              onClick={onNavigate}
            >
              <span>Allgemein (Alt)</span>
            </Link>
          </div>
        </div>
      </li>

      <SidebarNavItem
        to="/dashboard/parking_slots"
        icon={faSquareParking}
        label="Stellplätze"
        visible={parkingSlotsEnabled}
        onNavigate={onNavigate}
      />

      <hr className="sidebar-divider d-none d-md-block" />

      {/* <!-- Sidebar Toggler (Sidebar) --> */}
      <div className="sidebar-toggle-wrapper text-center d-none d-md-inline">
        <button
          id="sidebarToggle"
          onClick={() => setToggled(!toggled)}
          className="rounded-circle border-0"
        >
          <FontAwesomeIcon
            icon={toggled ? faChevronRight : faChevronLeft}
            style={{ color: "white" }}
          />
        </button>
      </div>
    </ul>
  );
};

export default Sidebar;
